import React, { useState, useEffect } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { Loader } from 'components/Loader';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { Search } from 'components/Search';
import { useAuth } from 'lib/auth';
import { DetailViewTabList } from 'components/TabList';
import { useSubscribers } from '../api/getAllSubscribers';
import { SubscribersListItem } from '../components/SubscribersListItems';
import { useDeleteSubscriber } from '../api/deleteSubscriber';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { useSubscriberCount } from '../api/getSubscriberCount';

export const List = () => {
  const { account } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('blog');
  const [markedForDelete, setMarkedForDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState(null);

  const {
    data: { subscribers, total },
    isFetching,
  } = useSubscribers({
    page: page - 1,
    limit: pageSize,
    type: currentTab,
    search,
    queryOptions: { 
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      }
    },
  });

  const { data: blogCount } = useSubscriberCount('blog');
  const { data: jobAdCount } = useSubscriberCount('job_ad');

  const { mutateAsync } = useDeleteSubscriber();

  const handlePageChange = (_, page) => setPage(page);

  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  const labelName = 'Newsletter Subscribers';

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
      setLoading(false);
    }
    return () => clearTimeout(timer);
  }, [isFetching]);

  return (
    <>
      <DeleteConfirmationModal
        markedForDelete={markedForDelete}
        mutateAsync={mutateAsync}
        onClose={() => setMarkedForDelete(null)}
      />
      {account.super_admin && (
        <ContentLayout>
          <Stack height={1}>
            <Stack>
              <BreadCrumbs items={[{ name: '', to: '' }, { name: labelName }]} />
            </Stack>
            
            <Box
              sx={{
                height: 16,
                backgroundColor: "background.paper",
              }}
            />

            <DetailViewTabList
              value={currentTab}
              onChange={(_, newTab) => {
                setPage(1);
                setCurrentTab(newTab);
              }}
              tabs={[
                { label: 'Blog', value: 'blog', count: blogCount },
                { label: 'Job Ad', value: 'job_ad', count: jobAdCount },
              ]}
            />

            <Stack
              px={4}
              py={2}
              bgcolor="background.paper"
              borderTop="1px solid"
              borderColor="border.main"
              height="100%"
            >
              <Search handleSearchChange={handleSearchChange} sx={{ minWidth: 200, width: 270, mb: 2 }} />

              {isFetching || loading ? (
                <Loader />
              ) : !subscribers || subscribers.length === 0 ? (
                <Stack
                  height="100%"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h5">No {labelName} added</Typography>
                </Stack>
              ) : (
                <>
                  <Stack
                    flex={1}
                    py={2}
                    bgcolor="background.paper"
                    borderTop="1px solid"
                    borderColor="border.main"
                    gap={1}
                  >
                    {subscribers.map((item) => (
                      <SubscribersListItem
                        key={item.subscriber_id}
                        {...item}
                        markForDelete={() =>
                          setMarkedForDelete({ id: item.subscriber_id, email: item.email })
                        }
                      />
                    ))}
                  </Stack>
                  <Stack direction="row" justifyContent="center">
                    <Pagination
                      page={page}
                      handlePageChange={handlePageChange}
                      itemCount={total}
                      itemsPerPage={pageSize}
                      onItemsPerPageChange={setPageSize}
                    />
                  </Stack>
                </>
              )}
            </Stack>
          </Stack>
        </ContentLayout>
      )}
    </>
  );
};
